/**
 * Cross-package runtime constants — the ONE source for every enum, status set,
 * and shipping value that used to be hand-mirrored across the API, the SPA, the
 * MCP tool schemas, and the Pages Functions (bead outstanding-49n.3a).
 *
 * Before this module those lists lived in 3-4 places each, kept honest only by
 * the packages/shared/contracts/*.json fixtures and their per-package twin
 * tests (bead qbo.5). Now that @outstanding/shared is a real linked runtime
 * package (workspaces migration, epic 49n), every package imports the SAME
 * array, so drift is impossible by construction and those tripwires retire.
 *
 * Zod-free ON PURPOSE: this module is bundled into the SPA (vite) and the Pages
 * Functions as well as the API and MCP, so it must not pull a validation library
 * into the storefront bundle. The `satisfies` + `Assert` guards below are
 * compile-time only and cost nothing at runtime.
 */

/** Compile-time exhaustiveness: fails to build unless A and B are the same union. */
type Assert<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

// ─── Product options ─────────────────────────────────────────────────────────

/** Every product-option category (bead hgd.8a). */
export const OPTION_CATEGORIES = [
  'size',
  'framed_size',
  'frame',
  'matte',
  'print_type',
] as const;
export type OptionCategory = (typeof OPTION_CATEGORIES)[number];

/**
 * Categories whose price_modifier IS the whole sellable price of the print:
 * photo.base_price is 0 post-cutover and pricing just sums the selected
 * options, so a `size`/`framed_size` row priced at 0 is a print that sells for
 * nothing. Their price must be POSITIVE everywhere — on create, on update, and
 * at the point of sale — and their cost basis must be positive too.
 *
 * Every other category is an add-on priced as a single upgrade DELTA, where 0
 * is legitimate (a standard finish or a no-matte choice is a real option at no
 * extra charge), so they are deliberately not in this set.
 */
export const PRICE_BEARING_OPTION_CATEGORIES = ['size', 'framed_size'] as const;
export type PriceBearingOptionCategory = (typeof PRICE_BEARING_OPTION_CATEGORIES)[number];

/**
 * Categories that must STATE a cost_cents on create, so the margin floor has a
 * basis to check against (bead outstanding-hpj4). These are physical Nations
 * products with a real per-unit COGS — framing runs $59-216 and mats $11-31
 * (docs/ARCHITECTURE.md); without a stated cost the floor silently allows any
 * price. `print_type` is a look choice with no separate line cost and stays out.
 *
 * Distinct from PRICE_BEARING_OPTION_CATEGORIES on purpose: a stated cost of 0
 * is legitimate here. `framed_size` already carries the Modern Wood frame and
 * standard mat cost, so the INCREMENTAL cost of a standard frame colour really
 * is 0 — and 0 cost with a 0 price passes the floor, which is exactly right.
 * Every price-bearing category is also cost-basis-bearing (asserted below).
 */
export const COST_BASIS_OPTION_CATEGORIES = [
  'size',
  'framed_size',
  'frame',
  'matte',
] as const;
export type CostBasisOptionCategory = (typeof COST_BASIS_OPTION_CATEGORIES)[number];

/** Compile-time: the price-bearing set must stay a subset of the costed set. */
const _priceBearingIsCosted: PriceBearingOptionCategory extends CostBasisOptionCategory
  ? true
  : false = true;
void _priceBearingIsCosted;

/**
 * What a cart line may carry beside its one price-bearing size — the ONE
 * statement of which add-on categories belong to which product line
 * (bead outstanding-e5pn). Framed and unframed are distinct products with
 * distinct price ladders (docs/ARCHITECTURE.md): the framed premium lives
 * entirely on the `framed_size` row and the frame colours are $0, so a
 * `frame` riding on a `size` row is a framed print bought at the unframed
 * price. The storefront (Detail.vue) only ever posts `size` + `print_type` or
 * `framed_size` + `frame` + `matte`; this is exactly what the API accepts:
 *
 *   size        — must carry a `print_type` (Lustre/Pearl/Glossy). The
 *                 storefront has required both unframed choices since 2022,
 *                 before carts ever stored option ids (2025-11), so no
 *                 persisted cart holds a size-only line; and a finish's price
 *                 is a catalog decision (Lustre is $0 today, not by contract),
 *                 so a line that omits it under-prices by whatever the default
 *                 finish is repriced to.
 *   framed_size — must carry BOTH a `frame` and a `matte` (No Matte is an
 *                 explicit $0 row), and nothing else: framed paper is always
 *                 Lustre, so a `print_type` here is not a choice on offer.
 *
 * `validateLineItems` (apps/api pricing-service) enforces this per item.
 */
export const LINE_ITEM_COMPOSITION = {
  size: { required: ['print_type'], optional: [] },
  framed_size: { required: ['frame', 'matte'], optional: [] },
} as const satisfies Record<
  PriceBearingOptionCategory,
  { required: readonly OptionCategory[]; optional: readonly OptionCategory[] }
>;

// ─── Print resolution ───────────────────────────────────────────────────────

/**
 * Minimum source resolution accepted for a sellable print size.
 *
 * Nations Photo Lab publishes 125 PPI as its floor. This is the THIRD copy of
 * that number: `apps/exposure/src/lib/labs.ts` holds it as `NATIONS_LUSTRE.minPpi`
 * (transcribed from the lab's spec sheet), and `apps/exposure/scripts/triage-photos.js`
 * mirrors it as `LAB_MIN_PPI` — that mirror IS pinned, by
 * `apps/exposure/tests/triage-thresholds.spec.ts`.
 *
 * This copy is NOT pinned, and cannot be by an import: Exposure is deliberately
 * standalone (near-zero deps, open-sourceable on its own), so neither package may
 * depend on the other. A lab change would therefore desync the store gate from the
 * exposure gate silently — which is the failure mode outstanding-bxoc exists to
 * fix. Do not "solve" this by adding a dependency in either direction.
 *
 * If the lab's floor changes, all three must move together.
 */
export const PRINT_MIN_PPI = 125;

/**
 * Print-size dimensions the store may price through `size` / `framed_size`
 * product options. Labels are normalized `WxH` tokens; product option display
 * names may add inch marks or words like "Framed".
 */
export const STORE_PRINT_SIZES = [
  { label: '8x10', widthIn: 8, heightIn: 10 },
  { label: '11x14', widthIn: 11, heightIn: 14 },
  { label: '8x12', widthIn: 8, heightIn: 12 },
  { label: '12x18', widthIn: 12, heightIn: 18 },
  { label: '16x20', widthIn: 16, heightIn: 20 },
  { label: '16x24', widthIn: 16, heightIn: 24 },
  { label: '20x24', widthIn: 20, heightIn: 24 },
  { label: '20x30', widthIn: 20, heightIn: 30 },
  { label: '24x36', widthIn: 24, heightIn: 36 },
  { label: '30x45', widthIn: 30, heightIn: 45 },
] as const;
export type StorePrintSize = (typeof STORE_PRINT_SIZES)[number];
export type StorePrintSizeLabel = StorePrintSize['label'];

/**
 * Which unframed print sizes each finish (`print_type` row, keyed by its
 * lower-cased name) is actually cut in — an ALLOWLIST, so a new size row is
 * offered in Lustre only until this table says otherwise (bead
 * outstanding-cnt7; the old storefront denylist failed open on growth).
 *
 * Source: docs/ARCHITECTURE.md, "Pearl/Glossy only 8x12 and 12x18 — Nations
 * doesn't cut them in 3:2 enlargements"; Lustre prints every size the store
 * sells. A finish name absent from this table is sellable in NO size until it
 * is added here — adding a print_type row is a catalog decision, not a data
 * entry. Read by `isPrintFinishSellableForSize`, which the API enforces in
 * `validateLineItems` and the detail page uses to gate the finish dropdown.
 */
export const PRINT_FINISH_SIZES = {
  lustre: STORE_PRINT_SIZES.map((size) => size.label),
  pearl: ['8x12', '12x18'],
  glossy: ['8x12', '12x18'],
} as const satisfies Record<string, readonly StorePrintSizeLabel[]>;

// ─── Status sets ─────────────────────────────────────────────────────────────

/** Order state machine. */
export const ORDER_STATUSES = [
  'NEW',
  'PROCESSING',
  'COMPLETED',
  'ERROR',
  'REFUNDED',
  'DISPUTED',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** Support-inquiry lifecycle. */
export const SUPPORT_STATUSES = ['NEW', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as const;
export type SupportStatus = (typeof SUPPORT_STATUSES)[number];

/** Editorial story draft → publish states. */
export const STORY_STATUSES = ['draft', 'published'] as const;
export type StoryStatus = (typeof STORY_STATUSES)[number];

/** Experiment lifecycle. */
export const EXPERIMENT_STATUSES = ['draft', 'running', 'paused', 'concluded'] as const;
export type ExperimentStatus = (typeof EXPERIMENT_STATUSES)[number];

// ─── Behavioral events ───────────────────────────────────────────────────────

/**
 * The client-ingestable event types — the exact set the SPA emits and the API
 * sink (routes/events.ts) accepts. Money-bearing types (purchase/refund/…) are
 * server-emitted and deliberately NOT here: a client-supplied `purchase` must be
 * 204-dropped, never recorded. An SPA-only addition that the sink doesn't accept
 * silently loses a funnel signal — this shared list is what makes that
 * impossible.
 */
export const CLIENT_EVENT_TYPES = [
  'product_view',
  'collection_view',
  'story_view',
  'add_to_cart',
  'exposure',
] as const;
export type ClientEventType = (typeof CLIENT_EVENT_TYPES)[number];

// ─── Shipping ────────────────────────────────────────────────────────────────

/** Shipping-speed codes (UPPERCASE). */
export const SHIPPING_SPEEDS = ['FREE', 'GROUND', 'PRIORITY'] as const;
export type ShippingSpeed = (typeof SHIPPING_SPEEDS)[number];

/** Shipping price per speed, in integer cents. */
export const SHIPPING_COSTS = {
  GROUND: 1000, // $10.00
  PRIORITY: 2500, // $25.00
  FREE: 0, // $0.00
} as const satisfies Record<ShippingSpeed, number>;

/**
 * Cart subtotal (cents, before shipping/tax) at or above which FREE ground
 * shipping is offered — "free ground shipping on orders of $50 or more".
 * Enforced server-side in the shipping-speed handler; the SPA only disables
 * the radio and states the threshold in copy.
 */
export const FREE_SHIPPING_MIN_SUBTOTAL = 5000; // $50.00

/**
 * Full per-speed shipping detail: display name, price (cents), and transit
 * window. The API prices orders from this and serves it via GET /shipping-options;
 * the SPA states the same durations/prices in static copy (src/content/
 * shippingFacts.ts) instead of hand-writing "$10" / "5-7 business days".
 */
export const SHIPPING_SPEED_DETAILS = {
  GROUND: {
    name: 'Ground Shipping',
    cost: SHIPPING_COSTS.GROUND,
    days: '5-7 business days',
  },
  PRIORITY: {
    name: 'Priority Shipping',
    cost: SHIPPING_COSTS.PRIORITY,
    days: '2-3 business days',
  },
  FREE: {
    name: 'Free Shipping',
    cost: SHIPPING_COSTS.FREE,
    // FREE is the same ground service as GROUND — the transit window matches.
    days: '5-7 business days',
  },
} as const satisfies Record<ShippingSpeed, { name: string; cost: number; days: string }>;

// ─── Experiment surfaces ─────────────────────────────────────────────────────

/**
 * Every experiment surface the engine knows. A surface names WHERE on the
 * storefront an experiment renders; the partitions below say how each is
 * consumed. Hand-mirrored before this in four places (the API type union, the
 * API zod enum, the MCP tool enums, and the fixture).
 */
export const EXPERIMENT_SURFACES = [
  'homepage_hero',
  'collection_order',
  'collection_content',
  'stories',
  'detail_options',
  'promo_banner',
  'cart_nudge',
  'pricing_display',
  'success_page',
  'pricing',
  'recommendations',
] as const;
export type ExperimentSurface = (typeof EXPERIMENT_SURFACES)[number];

/**
 * Surfaces whose assignment PAYLOAD a consumer renders (one
 * getAssignmentBySurface caller each): Homepage.vue (homepage_hero),
 * PromoBanner.vue (promo_banner), Cart.vue (cart_nudge), usePhotoLoader.ts
 * (detail_options), DetailRecommendations.vue (recommendations),
 * PriceDisplay.vue (pricing_display), Success.vue (success_page). The API
 * attaches a per-surface zod payload contract to each (experiment-contracts.ts).
 */
export const PAYLOAD_CONSUMER_SURFACES = [
  'homepage_hero',
  'promo_banner',
  'cart_nudge',
  'detail_options',
  'recommendations',
  'pricing_display',
  'success_page',
] as const;
export type PayloadConsumerSurface = (typeof PAYLOAD_CONSUMER_SURFACES)[number];

/**
 * Key-gated surfaces: the consumer ignores payloads and matches HARDCODED
 * literals — Story.vue checks assignments["story_template"] !== "essay". An
 * experiment with any other key/variant set never gates anything, so the
 * surface is contract-bound to that key + variant set.
 */
export const KEY_GATED_SURFACES = ['stories'] as const;
export type KeyGatedSurface = (typeof KEY_GATED_SURFACES)[number];

/**
 * Surfaces with NO SPA consumer (no getAssignmentBySurface caller, no key-gated
 * component): a RUNNING experiment here splits traffic and accumulates nothing,
 * so create-as-running is rejected. Drafts are allowed so design work isn't
 * blocked. collection_content joined this set 2026-08-15 (bead outstanding-7jzh)
 * when the collection FAQ went unconditional and CollectionFaq.vue stopped
 * gating on the collection_faq key; the surface stays in EXPERIMENT_SURFACES so
 * the historical experiment row on it still reads, lists, and concludes.
 */
export const CONSUMERLESS_SURFACES = ['collection_order', 'collection_content', 'pricing'] as const;
export type ConsumerlessSurface = (typeof CONSUMERLESS_SURFACES)[number];

// The three partitions must together cover EXPERIMENT_SURFACES exactly, with no
// overlap — a surface that is in none renders nothing and can't be created
// running; one in two is a contradiction. Checked at shared's own compile.
type _SurfacePartition =
  | PayloadConsumerSurface
  | KeyGatedSurface
  | ConsumerlessSurface;
const _surfacesPartitioned: Assert<ExperimentSurface, _SurfacePartition> = true;
void _surfacesPartitioned;

// ─── Experiment payload field values ────────────────────────────────────────

/**
 * The closed literal sets an experiment variant payload's enum-ish fields may
 * take. Each was hand-mirrored in TWO places with nothing but review keeping
 * them equal (bead outstanding-9rgp.10): the create-time zod enum in
 * @outstanding/shared/schemas (experiment-payloads.ts) and the SPA hand parser
 * that renders the surface. Both sides now read THESE arrays, so a literal the
 * API accepts but the SPA drops (or vice versa) cannot exist.
 *
 * `oneOf` below lives here rather than in the SPA because it is the reader for
 * these arrays: a parser that narrows through it never restates the literals.
 */

/** promo_banner `tone` — PromoBanner.vue's (quiet) color treatment. */
export const PROMO_BANNER_TONES = ['info', 'promo'] as const;
export type PromoBannerTone = (typeof PROMO_BANNER_TONES)[number];

/** detail_options `default_product_tab` — which product type (tab) shows first. */
export const DETAIL_PRODUCT_TABS = ['framed', 'unframed'] as const;
export type DetailProductTab = (typeof DETAIL_PRODUCT_TABS)[number];

/** recommendations `strategy` — the SOURCE of the "Pairs well with" rail. */
export const RECOMMENDATION_STRATEGIES = [
  'same_collection',
  'same_tags',
  'curated',
] as const;
export type RecommendationStrategy = (typeof RECOMMENDATION_STRATEGIES)[number];

/** pricing_display `from_price_format` — the wording of the "from $X" line. */
export const PRICING_FROM_FORMATS = ['from', 'starting_at', 'plus'] as const;
export type PricingFromFormat = (typeof PRICING_FROM_FORMATS)[number];

/**
 * `value` when it is one of `allowed`, else undefined — the zod-free narrowing
 * the SPA payload parsers use on server-authored `unknown` input. Compile-time
 * this returns the literal union, so a parser can assign the result straight
 * into a typed payload field without a cast; runtime it is a membership test,
 * NOT validation of anything else about the payload.
 */
export function oneOf<T extends string>(
  value: unknown,
  allowed: readonly T[]
): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

// ─── Payload / array caps ────────────────────────────────────────────────────
// Each bound was enforced by hand in 2-3 places (an API zod schema, an SPA
// clamp, sometimes the events sink) and pinned only by the qbo.5 limits.json
// fixture. Centralized so the API, SPA, and sink share ONE number.

/** Maximum bound parameters accepted by a D1 statement. */
export const D1_MAX_BIND_PARAMS = 100;

/**
 * Max line items per cart. Caps the payload so an oversized cart is a clean 400
 * instead of overflowing D1's 100-bound-param limit in the IN(...) lookups
 * (getPhotosByIds / getOptionsByIds) — 50 × 20 options is far beyond any real
 * single-print order.
 */
export const CART_MAX_LINE_ITEMS = 50;

/** Max option ids per cart line item (see the D1 bound-param note above). */
export const CART_MAX_OPTION_IDS_PER_LINE_ITEM = 20;

/** Max tags stored on an order (they land in a JSON column — row-bloat guard). */
export const ORDER_MAX_TAGS = 20;

/** Max length of a single order tag. */
export const TAG_MAX_LENGTH = 100;

/**
 * Promo codes (bead outstanding-z9ox). Redemption is owned locally: the cart
 * validates a code against the promo_codes table, checkout computes the
 * discount on MERCHANDISE only and tax on the discounted base, and Stripe gets
 * finished line items (no promotion code on the session).
 */
/** Max length of a code the cart accepts (stored UPPERCASE). */
export const PROMO_CODE_MAX_LENGTH = 32;

/**
 * Why the cart API rejected a code (error.code on the 4xx). The SPA maps these
 * to customer copy; the API never sends a message the SPA has to parse.
 *   PROMO_INVALID   — unknown, inactive, or not yet started
 *   PROMO_EXPIRED   — past expires_at
 *   PROMO_EXHAUSTED — max_redemptions reached
 *   PROMO_TOTAL_TOO_SMALL — (checkout only) the discounted total is under
 *                           Stripe's minimum card charge
 */
export const PROMO_ERROR_CODES = [
  'PROMO_INVALID',
  'PROMO_EXPIRED',
  'PROMO_EXHAUSTED',
  'PROMO_TOTAL_TOO_SMALL',
] as const;
export type PromoErrorCode = (typeof PROMO_ERROR_CODES)[number];

/**
 * Sales-tax filing bases (bead outstanding-ycuz, anchor 2). The remittance
 * summary (`GET /api/admin/analytics/tax-remittance`, MCP `tax_remittance_summary`)
 * slices the ledger by the period the return covers; the API validates the
 * `basis` query against this list and the MCP tool schema enumerates it.
 * The default is QUARTERLY — CDTFA returns for that basis are due the last day
 * of the month after quarter end (Apr 30 / Jul 31 / Oct 31 / Jan 31). The
 * account's ASSIGNED basis is not yet confirmed by the owner; change the
 * default here once it is.
 */
export const TAX_FILING_BASES = ['quarterly', 'monthly', 'annual'] as const;
export type TaxFilingBasis = (typeof TAX_FILING_BASES)[number];
export const DEFAULT_TAX_FILING_BASIS: TaxFilingBasis = 'quarterly';

/** Max featured photos attachable to a story (D1 chunking bound). */
export const STORY_MAX_PHOTO_IDS = 100;

/**
 * Max length of a persisted attribution value (utm_*). Bounds what the SPA
 * forwards AND what the events sink stores in the indexed channel columns, so a
 * tampered query string can't write an arbitrarily long value.
 */
export const ATTRIBUTION_VALUE_MAX_LENGTH = 200;

/** Max length of an experiment key tag (event attribution + admin schema). */
export const EXPERIMENT_KEY_MAX_LENGTH = 100;

/** Max length of an experiment variant key tag. */
export const EXPERIMENT_VARIANT_KEY_MAX_LENGTH = 100;

/**
 * Max length of the stable browser visitor id used for experiment assignment
 * and event-session attribution. UUIDs are much shorter; this is a row/index
 * bloat guard for the public assignment and event endpoints.
 *
 * PINNED to the DEPLOYED migration 0025_experiment_assignments.sql CHECK
 * (`length(visitor_id) BETWEEN 1 AND 200`), which is immutable once applied.
 * Do NOT bump this constant on its own: the API would then accept ids whose
 * INSERT violates the DB constraint and 500s the public assignment endpoint.
 * Raising it requires a new migration that recreates the CHECK first.
 */
export const VISITOR_ID_MAX_LENGTH = 200;

// ─── Address validation ──────────────────────────────────────────────────────

/**
 * A US postal code: 5 digits, optionally `-` plus 4. The ONE definition — the
 * API cart address schema validates against it, tax-service canonicalizes
 * against it, and the SPA checkout gate enables the confirm button on it, so
 * the boundary, the lookup, and the client can never disagree about what a ZIP
 * is (beads outstanding-afkc, outstanding-mbcj).
 *
 * Match the API's `z.string().trim().regex(...)`: TRIM BEFORE TESTING. The
 * server canonicalizes a padded ZIP rather than rejecting it, so a client that
 * tests the raw value would be stricter than the server it is gating for.
 *
 * No `g` flag: this regex is shared and must be stateless across `.test()`
 * calls.
 */
export const US_POSTAL_CODE_RE = /^\d{5}(?:-\d{4})?$/;

/** Bound the client-generated identity echoed in cart line verdicts. */
export const CART_LINE_ITEM_ID_MAX_LENGTH = 128;
